import { Link } from "@tanstack/react-router";
import { AlertCircle, CalendarSearch, Settings } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";

import { AppShell } from "components/AppShell";
import type { EventCardTarget } from "components/EventSummaryCard";
import { SectionHeader } from "components/SectionHeader";
import { unpublishedNote } from "components/unpublished-note";
import { EmptyState, LoadingCards } from "components/states";
import { Button } from "components/ui/button";
import { SECTION_TITLES } from "../../copy/section-titles";

import { eventHasEnded } from "../events/event-past";
import { PastSection } from "../events/PastSection";
import { arrangeMemberships, cardTiming } from "./arrange-memberships";
import { EventHeroCard } from "./EventHeroCard";
import { EventRowCard } from "./EventRowCard";
import { AdminChip, ArchivedChip, RegisteredChip } from "./EventTileParts";
import type { MembershipCard } from "./schemas";
import { useMyMemberships } from "./use-my-memberships";
import { useNow, useToday } from "./use-today";

export function DashboardPage({
  uid,
  email,
  signOut,
}: {
  uid: string;
  email: string | null;
  signOut: () => void;
}) {
  const state = useMyMemberships(uid);
  const identity = { email };
  // Re-read when the app comes back to the front, not on a timer: a label that
  // flips under the reader's thumb is worse than one that updates on return.
  const today = useToday();
  // Whether an event is over is judged on its own zone, which a day string in
  // the reader's zone cannot tell, so the instant is read as well.
  const now = useNow();
  const [showAllPast, setShowAllPast] = useState(false);
  const { hero, rest, past, pastTotal, hasMorePast } = useMemo(
    () =>
      state.status === "success"
        ? arrangeMemberships(state.memberships, today, now, {
            showAll: showAllPast,
          })
        : {
            hero: null,
            rest: [],
            past: [],
            pastTotal: 0,
            hasMorePast: false,
          },
    [state, today, now, showAllPast],
  );

  return (
    <AppShell identity={identity} onSignOut={signOut} title="My Events">
      <div className="mx-auto w-full max-w-6xl">
        <SectionHeader
          kicker={SECTION_TITLES.myEvents.kicker}
          title={SECTION_TITLES.myEvents.title}
          className="mb-6"
        />

        {state.status === "loading" && <LoadingCards cards={3} mode="grid" />}

        {state.status === "error" && (
          <EmptyState
            icon={AlertCircle}
            title="Could not load events"
            testId="dashboard-error"
          >
            {state.error}
          </EmptyState>
        )}

        {state.status === "success" && state.memberships.length === 0 && (
          // A starting point, not a fault: the neutral icon and the primary
          // button say "here is what to do next". Most people with no events
          // are sent to All Events on sign-in (landing.ts), so whoever sees
          // this opened My Events on purpose.
          <EmptyState
            icon={CalendarSearch}
            title="No events yet"
            tone="bold"
            testId="dashboard-empty"
            action={
              <Button variant="cta" asChild>
                <Link to="/events" data-testid="dashboard-browse-link">
                  Browse all events
                </Link>
              </Button>
            }
          >
            You're not registered for any event. Browse the events you can join.
          </EmptyState>
        )}

        {state.status === "success" && state.memberships.length > 0 && (
          <div className="flex flex-col gap-4" data-testid="dashboard-cards">
            {hero && (
              <ManageableCard membership={hero}>
                <EventCard
                  membership={hero}
                  today={today}
                  ended={false}
                  variant="hero"
                />
              </ManageableCard>
            )}
            {rest.length > 0 && (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {rest.map((m) => (
                  <ManageableCard key={m.eventId} membership={m}>
                    <EventCard
                      membership={m}
                      today={today}
                      ended={false}
                      variant="row"
                    />
                  </ManageableCard>
                ))}
              </div>
            )}
            {past.length > 0 && (
              <PastSection
                total={pastTotal}
                hasMore={hasMorePast}
                expanded={showAllPast}
                onToggle={() => setShowAllPast((shown) => !shown)}
                testId="dashboard-past"
              >
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {past.map((m) => (
                    <ManageableCard key={m.eventId} membership={m}>
                      <EventCard
                        membership={m}
                        today={today}
                        ended={eventHasEnded(cardTiming(m), now)}
                        variant="row"
                      />
                    </ManageableCard>
                  ))}
                </div>
              </PastSection>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function EventCard({
  membership,
  today,
  ended,
  variant,
}: {
  membership: MembershipCard;
  today: string;
  /** Over in the event's own zone: the card says so, whatever the reader's day. */
  ended: boolean;
  variant: "hero" | "row";
}) {
  const slug = membership.eventSlug;
  const admin = membership.isEventAdmin === true;
  // The same meaning the card has always carried: this is a membership, so
  // the person is registered, and an administrator is told so as well.
  const badges = (
    <>
      <RegisteredChip />
      {admin && <AdminChip />}
      {membership.eventArchived === true && <ArchivedChip />}
    </>
  );
  const props = {
    event: {
      title: membership.eventTitle ?? "Untitled event",
      startDate: membership.eventStartDate,
      endDate: membership.eventEndDate,
      location: membership.eventLocation,
      image: membership.eventImage,
    },
    today,
    target: cardTarget(membership),
    // `eventSlug` is set only for a published event (event_card.py). The
    // admin is told it is not public yet and where publishing happens; a member,
    // whose card leads nowhere yet, is told why, so it does not read as
    // broken.
    unpublished: !slug,
    note: slug ? undefined : unpublishedNote(admin),
    badges,
  };

  return variant === "hero" ? (
    <EventHeroCard {...props} />
  ) : (
    <EventRowCard {...props} ended={ended} />
  );
}

/**
 * A published event's card opens the event page, as it does for everyone, so
 * its admin gets the administration page as a second way in. Outside the
 * card's own link, because a link inside a link is invalid HTML.
 */
function ManageableCard({
  membership,
  children,
}: {
  membership: MembershipCard;
  children: ReactNode;
}) {
  if (membership.isEventAdmin !== true || !membership.eventSlug) {
    return children;
  }
  return (
    <div className="flex h-full flex-col gap-2 [&>[data-variant]]:flex-1">
      {children}
      <Button asChild variant="outline" size="sm">
        <Link
          to="/admin/event/$eventId"
          params={{ eventId: membership.eventId }}
          data-testid={`manage-event-${membership.eventId}`}
        >
          <Settings className="h-4 w-4" />
          Manage event
        </Link>
      </Button>
    </div>
  );
}

/**
 * The page a membership card opens. `eventSlug` is set only once the event is
 * published (event_card.py), so an unpublished event has no event page yet.
 * Its admin still has somewhere to go: the administration page, where
 * publishing happens.
 */
function cardTarget(membership: MembershipCard): EventCardTarget | null {
  if (membership.eventSlug) {
    return { page: "public", slug: membership.eventSlug };
  }
  if (membership.isEventAdmin === true) {
    return { page: "admin", eventId: membership.eventId };
  }
  return null;
}
